import { Observable, Subject } from 'rxjs';
import { ContentControlItem } from '../item/content-control-item';
import { ContentControlledItemsSpecifications } from '../specifications/content-controlled-items-specifications';
import {
  ContentControlledEditingDirective,
  ContentControlledEditingDirectiveScreenshot,
  ContentControlledEditingDirectiveViewModel,
} from '../editing-directive';
import { ContentControlItemLinkedFile } from '../item/content-control-item-linked-file';
import { ContentControlledReportableEvent } from '../reportable-event/content-controlled-reportable-event';
import { ContentControlledKeyframe } from '../keyframe/content-controlled-keyframe';
import { SubtitleSegment } from '../../subtitle-segment';
import { IContentControlledItemSettings } from '../settings/i-content-controlled-item-settings';
import { ITimestampToTimelineConverter } from '../provider';

export abstract class IContentControlManager {
  itemUpdated: Subject<boolean>;
  itemSaved: Subject<boolean>;
  screenshotTaken: Subject<ContentControlledEditingDirectiveScreenshot>;
  screenshotQueue: Subject<ContentControlledEditingDirectiveScreenshot>;
  eventsUpdated: Subject<void>;
  selectedSegmentRemoved: Subject<number>;
  abstract searchItems(
    specification: ContentControlledItemsSpecifications,
  ): Promise<Array<ContentControlItem>>;
  abstract getItemByMediaId(mediaId: number): Promise<boolean>;
  abstract getItemByChannelUid(channelUid: string): Promise<boolean>;
  abstract save(): Promise<void>;
  abstract clear(): void;
  abstract undoOperation(): void;
  abstract redoOperation(): void;

  abstract setupAudio(ctx: AudioContext, gain: GainNode): void;
  abstract startBeep(frequency: number): void;
  abstract stopBeep(): void;

  abstract loadSubtitle(lang: string, url: string, fps: number): Promise<void>;
  abstract getSubtitleSegments(
    lang: string,
    startTimeInMs: number,
    endTimeInMs: number,
  ): Array<SubtitleSegment>;
  abstract getCurrentSubtitleSegments(
    lang: string,
    currentTimeInMs: number,
  ): Array<SubtitleSegment>;
  abstract adjustTimeToSubtitle(timeInMs: number, lang: string): number;

  abstract get hasChanges(): boolean;
  abstract get videoFps(): number;
  abstract get isVideoFPSIsDropFrame(): boolean;
  abstract get videoUrl(): string;
  abstract get isLive(): boolean;
  abstract get mediaName(): string;
  abstract get mediaStartTimeStampMs(): number;
  abstract get itemSettings(): IContentControlledItemSettings;
  abstract get editingDirectives(): Array<ContentControlledEditingDirective>;
  abstract get reportableEvents(): Array<ContentControlledReportableEvent>;
  abstract get audioFiles(): Array<ContentControlItemLinkedFile>;
  abstract get subtitleFiles(): Array<ContentControlItemLinkedFile>;
  abstract get selectedSegment(): ContentControlledEditingDirectiveViewModel;
  abstract get selectedEvent(): ContentControlledEditingDirective;
  abstract get selectedEventKeyframesTime(): Array<number>;
  abstract get selectedSegmentIsEvent(): boolean;

  abstract set selectedSegment(
    segment: ContentControlledEditingDirectiveViewModel,
  );
  abstract set updateSettings(settings: IContentControlledItemSettings);

  abstract createNewSegment(): ContentControlledEditingDirectiveViewModel;
  abstract updateSelectedSegment(
    tempSegment: ContentControlledEditingDirectiveViewModel,
    doAsyncTasks?: boolean,
  );
  abstract addEditingDirectiveEvent(event: ContentControlledEditingDirective);

  abstract upsertCutEditingDirectiveEvent(
    cutEvent: ContentControlledEditingDirective,
    eventsToChange: Array<ContentControlledEditingDirective>,
    selectedSubtitles: Array<string>,
    segment?: ContentControlledEditingDirectiveViewModel,
  ): void;

  abstract revertEditingDirectiveEvent(
    event: ContentControlledEditingDirective,
  );
  abstract updateEditingDirectiveEventFromSegment(
    segment: ContentControlledEditingDirectiveViewModel,
    keyframe?: ContentControlledKeyframe,
  );
  abstract removeEditingDirectiveEvent(
    event: ContentControlledEditingDirective,
    includeInHistory?: boolean,
  );
  abstract updateSelectedSegmentKeyframes(
    keyframes: Array<ContentControlledKeyframe>,
  ): void;
  abstract addEventKeyframe(
    eventId: number,
    keyframe: ContentControlledKeyframe,
  ): void;
  abstract updateSelectedEventKeyframe(
    keyframe: ContentControlledKeyframe,
  ): void;
  abstract removeSelectedEventKeyframe(
    keyframe: ContentControlledKeyframe,
  ): void;

  abstract renderItems(
    items: Array<ContentControlItem>,
  ): Promise<{ success: string[]; errors: string[] }>;

  abstract downloadRenderedVideo(
    items: Array<ContentControlItem>,
  ): Promise<{
    success: { url: string; name: string }[];
    errors: string[];
  }>;

  abstract get displayContentControlStudioTimeInFrames(): boolean;

  setLiveHlsProviderForScreenshotConversion?(
    provider: ITimestampToTimelineConverter | null,
  ): void;
}
